import React, { useEffect, useState } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";
import defaultProfileImage from "../../../images/defaultProfileImage.png";
import { base_url } from "../../../utils/AxiosConfig";

Font.register({
  family: "Manrope",
  fonts: [
    { src: "/fonts/Manrope-Regular.ttf" },
    { src: "/fonts/Manrope-Bold.ttf", fontWeight: "bold" },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: "Manrope",
    color: "#333",
  },

  header: {
    textAlign: "center",
    fontSize: 16,
    fontWeight: "bold",
    marginTop: 8,
    marginBottom: 18,
  },

  // ── Two-column layout ──────────────────────────────
  row: {
    flexDirection: "row",
  },

  // Left col = 50%, Right col = 50%
  col: {
    width: "50%",
  },

  colLeft: {
    width: "50%",
    paddingRight: 12,
  },

  colRight: {
    width: "50%",
    paddingLeft: 12,
    borderLeftWidth: 1,
    borderLeftColor: "#e5e7eb",
  },
  // ──────────────────────────────────────────────────

  leftImage: {
    width: "35%",
    alignItems: "center",
    marginBottom: 14,
  },

  profileImage: {
    width: 120,
    height: 120,
    objectFit: "cover",
    border: "1px solid #ccc",
  },

  rightInfo: {
    width: "65%",
    paddingLeft: 10,
  },

  infoRow: {
    flexDirection: "row",
    marginBottom: 5,
    alignItems: "flex-start",
  },

  label: {
    width: 120,
    fontSize: 9,
    color: "#666",
  },

  value: {
    flex: 1,
    fontSize: 9,
    fontWeight: "bold",
    color: "#111",
  },

  divider: {
    borderBottomWidth: 1,
    borderColor: "#e5e7eb",
    marginVertical: 10,
  },

  sectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 7,
    color: "#2663EB",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  sectionTitleDocuments: {
    fontSize: 10,
    fontWeight: "bold",
    marginTop: 17,
    marginBottom: 7,
    color: "#2663EB",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },

  footer: {
    position: "absolute",
    bottom: 15,
    left: 30,
    right: 30,
    textAlign: "center",
    fontSize: 8,
    color: "grey",
  },
});

// Reusable field row — label + value
const Field = ({ label, value }) => (
  <View style={styles.infoRow}>
    <Text style={styles.label}>{label}:</Text>
    <Text style={styles.value}>{value || "-"}</Text>
  </View>
);

const EditEmpDocument = ({ employeeData }) => {
  const bondTimeportNoData = JSON.parse(
    localStorage.getItem("BondTimeportNoData") || "[]",
  );

  const headerImage = bondTimeportNoData[0]?.REP_HEADER;
  const footerImage = bondTimeportNoData[0]?.REP_FOOTER;

  const d = employeeData || {};
  console.log("d:", d);
  const formatDate = (date) => (date ? dayjs(date).format("DD-MMM-YYYY") : "-");

  const [profileImage, setProfileImage] = useState(defaultProfileImage);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEmployeeImage(employeeData?.EmpId);
  }, []);

  const fetchEmployeeImage = async (empId) => {
    try {
      setLoading(true);
      setError(null);
      const storedUserDataArray =
        JSON.parse(localStorage.getItem("BondTimeuserDataArray")) || [];
      const companyCode = storedUserDataArray[0]?.cmpcode || null;
      const response = await fetch(
        `${base_url}/api/CpaysCount/${companyCode}/EMPIMAGE/${empId}/-`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const imageUrl = data?.[0]?.Column1;
      if (imageUrl && imageUrl.startsWith("data:image")) {
        setProfileImage(imageUrl);
      } else {
        setError("Invalid image format");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load image");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER IMAGE */}
        {headerImage && (
          <Image
            src={headerImage}
            style={{ width: "100%", height: 60, marginBottom: 8 }}
          />
        )}

        <Text style={styles.header}>Employee Report</Text>

        {/* ── PROFILE ROW ─────────────────────────────── */}
        <View style={styles.row}>
          <View style={styles.leftImage}>
            <Image
              source={{ uri: profileImage || defaultProfileImage }}
              style={styles.profileImage}
              resizeMode="cover"
              onError={() => setProfileImage(null)}
            />
          </View>

          <View style={styles.rightInfo}>
            <Text style={{ fontSize: 12, fontWeight: "bold", marginBottom: 8 }}>
              {d.FistName} {d.MiddleName} {d.LastName}
            </Text>
            <Field label="Employee ID" value={d.EmpId} />
            <Field label="Alternate ID" value={d.AlternateID} />
            <Field label="Department" value={d.Department} />
            <Field label="Job Title" value={d.JobTitle} />
            <Field label="Join Date" value={formatDate(d.JoinDate)} />
            <Field label="Status" value={d.Status} />
          </View>
        </View>

        <View style={styles.divider} />

        {/* ── PERSONAL INFORMATION ─────────────────────── */}
        <Text style={styles.sectionTitle}>Personal Information</Text>
        <View style={styles.row}>
          <View style={styles.colLeft}>
            <Field label="Gender" value={d.Gender} />
            <Field label="DOB" value={formatDate(d.DOB)} />
            <Field label="Nationality" value={d.Nationality} />
          </View>
          <View style={styles.colRight}>
            <Field label="Marital" value={d.MaritalStatus} />
            <Field label="Height" value={d.Height} />
            <Field label="Weight" value={d.Weight} />
          </View>
        </View>

        <View style={styles.divider} />

        {/* ── CONTACT DETAILS ──────────────────────────── */}
        <Text style={styles.sectionTitle}>Contact Details</Text>
        <View style={styles.row}>
          <View style={styles.colLeft}>
            <Field label="Personal Mobile" value={d.WorkPhone} />
            <Field label="Personal Email" value={d.PersonalEmail} />
            <Field label="Emergency Contact" value={d.PersonalMobile} />
            <Field label="Home Airport" value={d.HomeAirport} />
            <Field label="Home Address" value={d.HomeAddress} />
          </View>
          <View style={styles.colRight}>
            <Field label="Work Mobile" value={d.WorkMobile} />
            <Field label="Work Email" value={d.WorkEmail} />
            <Field label="Emergency Phone" value={d.PersonalPhone} />
            <Field label="Work Phone" value={d.WorkPhone} />
            <Field label="Current Address" value={d.CurrentAddress} />
          </View>
        </View>

        <View style={styles.divider} />

        {/* ── Employemnt Overview ──────────────────────────── */}
        <Text style={styles.sectionTitle}>Employment Overview</Text>
        <View style={styles.row}>
          <View style={styles.colLeft}>
            <Field label="Shift Group" value={d.ShiftGroup} />
            <Field label="Agent ID" value={d.AgentID} />
            {/* </View>
          <View style={styles.colRight}> */}
            <Field label="Job Title" value={d.JobTitle} />
            <Field label="Division" value={d.Division} />
          </View>
          <View style={styles.colRight}>
            <Field label="Job Type" value={d.JobType} />
            <Field label="Grade" value={d.Grade} />
            {/* </View>
          <View style={styles.colRight}> */}
            <Field label="Bank Name" value={d.BankName} />
            <Field label="Bank Account No / IBAN" value={d.BankAccountNo} />
          </View>
        </View>

        <View style={styles.divider} />

        {/* ── SALARY DETAILS ───────────────────────────── */}
        <Text style={styles.sectionTitle}>Salary Details</Text>
        <View style={styles.row}>
          <View style={styles.colLeft}>
            <Field label="Initial Basic Pay" value={d.BasicPay} />
            {/* <Field label="Increments Total" value={d.NormalHrs} /> */}
            <Field label="Net Basic Salary" value={d.NetSalary} />
            <Field label="Salary A/C" value={d.SalaryAc} />
            <Field label="Allowances" value={d.SalaryAc} />
          </View>
          <View style={styles.colRight}>
            <Field label="Normal Hrs" value={d.NormalHrs} />
            <Field label="Hourly Wage" value={d.HourlyWage} />
            <Field label="OT Rate" value={d.OTrate} />
            <Field label="Special OT Rate" value={d.OTrate} />
            <Field label="Net Salary" value={d.NetSalary} />
            {/* <Field label="Net Salary" value={d.NetSalary + d.Allowances + d.Increments } /> */}
            
          </View>
        </View>

        <View style={styles.divider} />

        {/* ── DOCUMENTS ────────────────────────────────── */}
        <Text style={styles.sectionTitleDocuments}>Documents</Text>
        <View style={styles.row}>
          <View style={styles.colLeft}>
            <Field label="Emirates ID" value={d.EmiratesId} />
            <Field label="Visa No" value={d.VisaNo} />

            <Field label="Driving Licence" value={d.DrivingLicenceNo} />
            <Field label="Insurance No" value={d.InsuranceNo} />
            <Field label="Labour Card" value={d.PersonId} />
            <Field label="Part-time Labour No" value={d.parttime} />
            <Field label="Passport No" value={d.PassportNo} />
            <Field
              label="Passport Issue"
              value={formatDate(d.PassportIssueDate)}
            />
            <Field
              label="Passport Country of Issue"
              value={d.PassportCountryofIssue}
            />
          </View>
          <View style={styles.colRight}>
            <Field
              label="Emirates Expiry"
              value={formatDate(d.EmiratesIdExpiryDate)}
            />
            <Field label="Visa Expiry" value={formatDate(d.VisaExpiryDate)} />

            <Field
              label="Licence Expiry"
              value={formatDate(d.DrivingLicenceExpiryDate)}
            />
            <Field
              label="Insurance Expiry"
              value={formatDate(d.InsuranceExpiryDate)}
            />
            <Field
              label="Labour Card Expiry"
              value={formatDate(d.PersonIdExpiryDate)}
            />
            <Field label="Part-time Expiry" value={formatDate(d.parttimeexp)} />
            <Field label="Place of Issue" value={d.PassportPlaceofIssue} />
            <Field
              label="Passport Expiry"
              value={formatDate(d.PassportExpiryDate)}
            />
          </View>
        </View>

        <View style={styles.divider} />

        {/* FOOTER IMAGE */}
        {footerImage && (
          <Image
            src={footerImage}
            style={{
              position: "absolute",
              bottom: 10,
              left: 30,
              right: 30,
              width: "100%",
              height: 50,
            }}
          />
        )}
      </Page>
    </Document>
  );
};

export default EditEmpDocument;
